import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Link,
    Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Transition } from "../../../../UiComponents/Transition/Transition";
import { ToastError, ToastSuccess } from "../../../../UiComponents/Toaster/Toast";
import {
    GetBulkUploadsWithValidationErrors,
    MarkValidationErrorsAsFinished,
    downloadFileByKey,
} from "../../../../Service/_index";
import dayjs from "dayjs";

const ValidationErrorsDialog = ({ open, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [bulkUploads, setBulkUploads] = useState([]);
    const [processing, setProcessing] = useState({});
    const [expanded, setExpanded] = useState({});
    const [confirmingResolveAll, setConfirmingResolveAll] = useState({});

    useEffect(() => {
        if (open) {
            fetchValidationErrors();
        }
    }, [open]);



    const fetchValidationErrors = async () => {
        setLoading(true);
        try {
            const response = await GetBulkUploadsWithValidationErrors();
            if (response?.error) {
                ToastError(response?.message || "Failed to fetch validation errors");
                setBulkUploads([]);
            } else {
                setBulkUploads(response?.data || []);
            }
        } catch (error) {
            ToastError("Failed to fetch validation errors");
            setBulkUploads([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkSingleAsFinished = async (bulkUploadId, errorIndex) => {
        const key = `${bulkUploadId}-${errorIndex}`;
        setProcessing((prev) => ({ ...prev, [key]: true }));
        console.log("bulkUploadId", bulkUploadId);
        console.log("errorIndex", errorIndex);
        try {
            const response = await MarkValidationErrorsAsFinished(bulkUploadId, errorIndex);
            if (response?.error) {
                ToastError(response?.message || "Failed to mark error as finished");
            } else {
                ToastSuccess("Error marked as resolved");
                await fetchValidationErrors();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            ToastError("Failed to mark error as finished");
        } finally {
            setProcessing((prev) => ({ ...prev, [key]: false }));
        }
    };

    const handleMarkAllAsFinished = async (bulkUploadId) => {
        // Check if we are in confirmation mode for this specific item
        if (!confirmingResolveAll[bulkUploadId]) {
            setConfirmingResolveAll((prev) => ({ ...prev, [bulkUploadId]: true }));
            // Optional: Auto-reset after 3 seconds if not clicked again
            setTimeout(() => {
                setConfirmingResolveAll((prev) => ({ ...prev, [bulkUploadId]: false }));
            }, 3000);
            return;
        }

        const key = `${bulkUploadId}-all`;
        setProcessing((prev) => ({ ...prev, [key]: true }));

        try {
            // Pass 'all' as the errorIndex to indicate resolving all
            const response = await MarkValidationErrorsAsFinished(bulkUploadId, 'all');
            if (response?.error) {
                ToastError(response?.message || "Failed to mark all errors as finished");
            } else {
                ToastSuccess("All errors marked as resolved");
                await fetchValidationErrors();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            ToastError("Failed to mark all errors as finished");
        } finally {
            setProcessing((prev) => ({ ...prev, [key]: false }));
            setConfirmingResolveAll((prev) => ({ ...prev, [bulkUploadId]: false }));
        }
    };

    const handleToggleExpand = (bulkUploadId) => {
        setExpanded((prev) => {
            const isCurrentlyExpanded = prev[bulkUploadId];
            // If strictly opening one closes others, return new object with just that one
            // If closing the open one, return empty
            return isCurrentlyExpanded ? {} : { [bulkUploadId]: true };
        });
    };

    const handleClose = () => {
        onClose();
    };

    const handleDownloadFile = async (bulkUpload) => {
        const fileKey = bulkUpload.file?.key;
        if (!fileKey) {
            ToastError("File key not found");
            return;
        }

        try {
            setLoading(true); // Optional: show loading state
            const response = await downloadFileByKey(fileKey);
            if (response?.data?.downloadURL) {
                window.open(response.data.downloadURL, "_blank");
            } else {
                // Fallback if no URL returned but no error (unexpected format)
                console.error("No download URL in response", response);
                ToastError("Failed to get download URL");
            }
        } catch (error) {
            console.error("Download error:", error);
            ToastError("Failed to download file");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: "1px solid #E5E7EB",
                py: 2,
                px: 3,
                backgroundColor: "#FFFFFF"
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600} color="#111827" fontSize="18px" fontFamily="Inter">
                        Not Created Policies
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            color: "#6B7280",
                            "&:hover": { backgroundColor: "#F3F4F6" }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3, mt: 2, backgroundColor: "#F9FAFB" }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                        <CircularProgress size={40} />
                    </Box>
                ) : bulkUploads.length === 0 ? (
                    <Box py={6} textAlign="center">
                        <Typography variant="body1" color="#6B7280" gutterBottom fontFamily="Inter">
                            No Validation Errors Found
                        </Typography>
                        <Typography variant="body2" color="#9CA3AF" fontSize="13px" fontFamily="Inter">
                            All bulk uploads have been processed successfully
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {bulkUploads.map((bulkUpload) => {
                            const unfinishedErrors = bulkUpload.validationErrors?.filter(
                                (error) => !error.isFinished
                            ) || [];

                            if (unfinishedErrors.length === 0) return null;

                            const isExpanded = expanded[bulkUpload._id];

                            return (
                                <Box key={bulkUpload._id} mb={2}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            border: "1px solid #E5E7EB",
                                            borderRadius: "6px",
                                            overflow: "hidden"
                                        }}
                                    >
                                        {/* Compact File Header */}
                                        <Box
                                            sx={{
                                                backgroundColor: "#FFFFFF",
                                                borderBottom: isExpanded ? "1px solid #E5E7EB" : "none",
                                                px: 2.5,
                                                py: 1.5,
                                                cursor: "pointer",
                                                transition: "background-color 0.15s",
                                                "&:hover": {
                                                    backgroundColor: "#F9FAFB"
                                                }
                                            }}
                                            onClick={() => handleToggleExpand(bulkUpload._id)}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Box flex={1} display="flex" alignItems="center" gap={2}>
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDownloadFile(bulkUpload);
                                                        }}
                                                        sx={{
                                                            color: "#2563EB",
                                                            fontWeight: 600,
                                                            fontSize: "14px",
                                                            textDecoration: "underline",
                                                            textAlign: "left",
                                                            "&:hover": {
                                                                color: "#1D4ED8"
                                                            }
                                                        }}
                                                    >
                                                        {bulkUpload.file?.originalFileName || bulkUpload.file?.fileName || "Unknown File"}
                                                    </Link>
                                                    <Typography variant="body2" color="#6B7280" fontSize="13px" fontFamily="Inter">
                                                        {bulkUpload.companyName}
                                                    </Typography>
                                                    <Typography variant="body2" color="#9CA3AF" fontSize="12px" fontFamily="Inter">
                                                        {dayjs(bulkUpload.createdAt).format("DD MMM YYYY")}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#DC2626",
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            fontFamily: "Inter",
                                                            backgroundColor: "#FEE2E2",
                                                            px: 1.5,
                                                            py: 0.25,
                                                            borderRadius: "4px"
                                                        }}
                                                    >
                                                        {unfinishedErrors.length} error{unfinishedErrors.length !== 1 ? 's' : ''}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color={confirmingResolveAll[bulkUpload._id] ? "warning" : "error"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMarkAllAsFinished(bulkUpload._id);
                                                        }}
                                                        disabled={processing[`${bulkUpload._id}-all`]}
                                                        sx={{
                                                            fontSize: "12px",
                                                            textTransform: "none",
                                                            mr: 1,
                                                            minWidth: "100px"
                                                        }}
                                                    >
                                                        {processing[`${bulkUpload._id}-all`]
                                                            ? "..."
                                                            : confirmingResolveAll[bulkUpload._id]
                                                                ? "Are you sure?"
                                                                : "Resolve All"}
                                                    </Button>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleExpand(bulkUpload._id);
                                                        }}
                                                        sx={{
                                                            color: "#6B7280",
                                                            "&:hover": {
                                                                backgroundColor: "#F3F4F6"
                                                            }
                                                        }}
                                                    >
                                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Compact Errors Table */}
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "#374151",
                                                                    fontSize: "11px",
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.5px",
                                                                    py: 1,
                                                                    width: "6%"
                                                                }}
                                                            >
                                                                Row
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "#374151",
                                                                    fontSize: "11px",
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.5px",
                                                                    py: 1,
                                                                    width: "12%"
                                                                }}
                                                            >
                                                                Field
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "#374151",
                                                                    fontSize: "11px",
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.5px",
                                                                    py: 1,
                                                                    width: "62%"
                                                                }}
                                                            >
                                                                Issue
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: "#374151",
                                                                    fontSize: "11px",
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.5px",
                                                                    py: 1,
                                                                    width: "20%",
                                                                    textAlign: "center"
                                                                }}
                                                            >
                                                                Action
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {unfinishedErrors.map((error, index) => {
                                                            const originalIndex = bulkUpload.validationErrors.findIndex(e => e._id.toString() === error._id.toString());
                                                            const key = `${bulkUpload._id}-${originalIndex}`;

                                                            return (
                                                                <TableRow
                                                                    key={index}
                                                                    sx={{
                                                                        "&:hover": { backgroundColor: "#F9FAFB" },
                                                                        "&:last-child td": { borderBottom: 0 }
                                                                    }}
                                                                >
                                                                    <TableCell
                                                                        sx={{
                                                                            color: "#111827",
                                                                            fontWeight: 600,
                                                                            fontSize: "13px",
                                                                            py: 1.5
                                                                        }}
                                                                    >
                                                                        {error.row || "-"}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: "#4B5563",
                                                                            fontSize: "13px",
                                                                            fontWeight: 500,
                                                                            py: 1.5
                                                                        }}
                                                                    >
                                                                        {error.field || "-"}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: "#6B7280",
                                                                            fontSize: "13px",
                                                                            py: 1.5
                                                                        }}
                                                                    >
                                                                        {error.error || "-"}
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "16px" }} />}
                                                                            onClick={() => handleMarkSingleAsFinished(bulkUpload._id, originalIndex)}
                                                                            disabled={processing[key]}
                                                                            sx={{
                                                                                borderColor: "#D1D5DB",
                                                                                color: "#374151",
                                                                                textTransform: "none",
                                                                                fontWeight: 500,
                                                                                fontSize: "12px",
                                                                                fontFamily: "Inter",
                                                                                px: 1.5,
                                                                                py: 0.5,
                                                                                minWidth: "90px",
                                                                                borderRadius: "6px",
                                                                                "&:hover": {
                                                                                    borderColor: "#10B981",
                                                                                    backgroundColor: "#ECFDF5",
                                                                                    color: "#059669"
                                                                                },
                                                                                "&:disabled": {
                                                                                    borderColor: "#E5E7EB",
                                                                                    color: "#9CA3AF"
                                                                                }
                                                                            }}
                                                                        >
                                                                            {processing[key] ? "..." : "Resolve"}
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Collapse>
                                    </Paper>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                borderTop: "1px solid #E5E7EB",
                p: 2,
                backgroundColor: "#FFFFFF"
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "13px",
                        fontFamily: "Inter",
                        px: 2.5,
                        py: 0.75,
                        borderRadius: "6px",
                        "&:hover": {
                            borderColor: "#9CA3AF",
                            backgroundColor: "#F9FAFB"
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ValidationErrorsDialog;
